import React, { useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { SERVICES, PACKAGES } from "../../data/staticData";
import { calcTotal, estimatedCompletionDate } from "../../lib/pricing";
import { api } from "../../lib/api";
import { ChevronLeft, ArrowUpRight, Lock, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function PackageOrder() {
  const { slug, packageId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled") === "1";
  const service = SERVICES.find((s) => s.slug === slug);
  const pkg = (PACKAGES[slug] || []).find((p) => p.id === packageId);
  const [loading, setLoading] = useState(false);

  if (!service || !pkg) {
    return (
      <DashboardLayout>
        <div className="text-sm text-inkSoft">Package not found. <Link to="/dashboard/services" className="text-terra">Back to catalog</Link></div>
      </DashboardLayout>
    );
  }

  const total = calcTotal(pkg.price);
  const eta = estimatedCompletionDate(pkg.turnaround);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Link to={`/dashboard/services/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-inkSoft hover:text-ink mb-4"><ChevronLeft size={14} /> {service.title}</Link>

      {cancelled && (
        <div className="mb-4 rounded-2xl border border-line bg-white px-5 py-4 flex items-center gap-3" data-testid="payment-cancel-banner">
          <XCircle size={18} className="text-terra" />
          <div>
            <div className="text-sm font-medium">Payment cancelled</div>
            <div className="text-xs text-inkSoft">Your card wasn&apos;t charged. You can pay any time to start this order.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="package-order-form">
        {/* What you get */}
        <div className="lg:col-span-2 rounded-2xl border border-line bg-white overflow-hidden">
          <div className="bg-ink text-white px-6 py-5">
            <h1 className="font-display text-xl font-semibold tracking-tight" data-testid="package-order-title">{service.title}: {pkg.name} Package</h1>
            <p className="text-white/70 text-xs mt-1">One-time payment · brief comes next</p>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <div className="eyebrow mb-2">What&apos;s included</div>
              <ul className="space-y-2 text-sm text-ink/85">
                {(pkg.features || []).map((f) => (
                  <li key={f} className="flex gap-2"><span className="text-terra">✓</span> {f}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-2">How this works</div>
              <ol className="space-y-2 text-sm text-inkSoft list-decimal list-inside">
                <li>Pay securely via Stripe (any card, instant).</li>
                <li>You&apos;ll be redirected back to fill your project brief.</li>
                <li>Our team picks it up within one business day.</li>
                <li>Track everything from your dashboard.</li>
              </ol>
            </div>
            <div className="rounded-xl bg-surfaceAlt/50 border border-line px-4 py-3 flex items-center gap-2 text-xs text-inkSoft">
              <Lock size={14} className="text-terra" /> Secured by Stripe. We never see or store your card details.
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <aside className="rounded-2xl border border-line bg-white overflow-hidden h-max lg:sticky lg:top-6" data-testid="order-summary">
          <div className="bg-ink text-white px-6 py-5">
            <h2 className="font-display text-lg font-semibold tracking-tight">Order Summary</h2>
          </div>
          <div className="p-6 divide-y divide-line">
            <Row k={<span><span className="font-medium">{pkg.name}</span> Package</span>} v={`$${pkg.price.toFixed(2)}`} highlight />
            <Row k="Amount you&apos;ll pay" v={<span className="font-mono text-2xl font-semibold tracking-tighter">${total.toFixed(2)}</span>} />
            <Row k="Estimated completion" v={eta} />
          </div>
          <div className="p-6 pt-2">
            <button
              type="button"
              onClick={startCheckout}
              disabled={loading}
              className="btn-primary w-full justify-center text-sm"
              data-testid="package-order-submit"
            >
              {loading ? "Redirecting…" : (<><Lock size={14} /> Pay & continue <ArrowUpRight size={16} /></>)}
            </button>
            <p className="text-[11px] text-inkSoft mt-3 text-center">Brief form opens after payment. Track your order from the dashboard.</p>
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className={`flex items-center justify-between py-3 ${highlight ? "bg-terra/5 px-3 -mx-3 rounded-lg" : ""}`}>
      <span className="text-sm text-ink/80">{k}</span>
      <span className={`text-sm ${highlight ? "font-semibold text-ink" : "font-medium text-ink"} font-mono`}>{v}</span>
    </div>
  );
}
